import { apiSlice } from './apiSlice';

/* =========================
   TYPES
========================= */
export interface CreateGroupPayload {
  title: string;
  description: string;
  categoryId: number;
  image: string;
}

export interface Group {
  id: number;
  title: string;
  description: string;
  categoryId: number;
  image: string | null;
  groupStatus: 'Active' | 'Inactive';
  createdAt: string;
  updatedAt?: string;
  _count: {
    groupMember: number;
    sharedAssetGroups: number;
  };
  category: {
    id: number;
    name: string;
  };
}

export interface GroupResponse {
  count: number;
  skip: number;
  take: number;
  data: Group[];
}

/* =========================
   API
========================= */
export const allGroupApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ✅ GET GROUPS */
    getGroups: builder.query<
      GroupResponse,
      {
        search?: string;
        fromDate?: string;
        toDate?: string;
        skip?: number;
        take?: number;
        groupStatus?: string;
      }
    >({
      query: ({ search, fromDate, toDate, skip, take, groupStatus }) => ({
        url: '/group',
        params: { search, fromDate, toDate, skip, take, groupStatus },
      }),
      providesTags: ['Group'],
    }),
    getGroupById: builder.query<Group, number>({
      query: (id) => `/group/${id}`,
    }),

    getGroupMemberById: builder.query<
      any,
      {
        groupId: number;
        skip?: number;
        take?: number;
        search?: string;
      }
    >({
      query: ({ groupId, skip = 0, take = 10, search }) => ({
        url: `/group/group-member/${groupId}`,
        params: {
          skip,
          take,
          ...(search ? { search } : {}),
        },
      }),

      providesTags: ['GroupMembers'], // ✅ ADD THIS
    }),

    getIntrestedMember: builder.query<
      any,
      {
        groupId: number;
        assetId: number;
        skip?: number;
        take?: number;
        search?: string;
      }
    >({
      query: ({ groupId, skip = 0, take = 10, search, assetId }) => ({
        url: `/share-asset/${groupId}/interest-list/${assetId}`,
        params: {
          skip,
          take,
          ...(search ? { search } : {}),
        },
      }),

      providesTags: ['GroupMembers'], // ✅ ADD THIS
    }),

    getAssetsInGroup: builder.query<
      any,
      {
        groupId: number;
        search?: string;
        skip?: number;
        take?: number;
        status?: 'PENDING' | 'APPROVED' | 'REJECTED';
      }
    >({
      query: ({ groupId, search, skip = 0, take = 10, status }) => ({
        url: `/group/assets-in-group/${groupId}`,
        params: {
          skip,
          take,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}), // ✅ only send when filter applied
        },
      }),

      providesTags: ['GroupAssets'], // optional but recommended
    }),

    /* ✅ CREATE GROUP */
    createGroupApi: builder.mutation<Group, CreateGroupPayload>({
      query: (body) => ({
        url: '/group',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Group'],
    }),

    /* ✅ DELETE GROUP */
    deleteGroup: builder.mutation<void, number>({
      query: (groupId) => ({
        url: `/group/${groupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Group'], // auto refresh
    }),

    /* ✅ UPDATE STATUS */
    updateGroupStatus: builder.mutation<void, { id: number; status: 'Active' | 'Inactive' }>({
      query: ({ id, status }) => ({
        url: `/group/${id}/${status}`,
        method: 'POST', // backend agar POST use kare to change kar lena
      }),
      invalidatesTags: ['Group'],
    }),
    /* ✅ REMOVE GROUP MEMBER */
    removeGroupMember: builder.mutation<void, { groupId: number; accountId: number }>({
      query: ({ groupId, accountId }) => ({
        url: `/join-group/${groupId}/member/${accountId}`,
        method: 'DELETE',
      }),

      invalidatesTags: ['GroupMembers'], // ✅ MATCH
    }),

    updateSharedAsset: builder.mutation<
      any,
      {
        sharedAssetId: number;
        status: 'APPROVED' | 'REJECTED';
        tokenAmount?: number;
        rejectNote?: string;
      }
    >({
      query: (body) => ({
        url: '/share-asset',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['GroupAssets'],
    }),
    shareAssetInGroup: builder.mutation<
      any,
      {
        sharedAssetId: number;
        groupIds: number[];
      }
    >({
      query: (body) => ({
        url: '/share-asset/in-group',
        method: 'POST',
        body,
      }),

      invalidatesTags: ['GroupAssets'], // optional but recommended
    }),
    getGroupListByAsset: builder.query<
      any,
      {
        assetId: number;
      }
    >({
      query: ({ assetId }) => ({
        url: `/share-asset/group-list/${assetId}`,
      }),

      providesTags: ['GroupAssets'], // optional
    }),
  }),
});

/* =========================
   EXPORT HOOKS
========================= */
export const {
  useGetGroupsQuery,
  useGetGroupByIdQuery,
  useGetGroupMemberByIdQuery,
  useGetIntrestedMemberQuery,
  useCreateGroupApiMutation,
  useDeleteGroupMutation,
  useUpdateGroupStatusMutation,
  useRemoveGroupMemberMutation,
  useGetAssetsInGroupQuery,
  useUpdateSharedAssetMutation,
  useShareAssetInGroupMutation,
  useGetGroupListByAssetQuery,
} = allGroupApi;
